import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Modal, message } from 'antd';
import { EMI } from 'shared/model/types';
import { getEMIAccountMeta } from 'shared/config/store/reducers/accountsSlice';
import { RootState } from 'shared/config/store/store';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { VerificationStatus } from 'entities/user/model/types';
import { useLazyGetAccountUserMetaQuery } from 'entities/account/api';
import { OnfidoVerification } from 'features/account/add-account/ui/OnfidoVerification';
import VerificationStatusModal from 'pages/dashboard/components/VerificationStatusModal';

type VerificationWidgetProps = {
  emi: EMI;
};

type VerificationContextType = {
  openOnfidoModal: () => void;
  onfidoClosedWithoutCompletion: boolean;
  emi: EMI;
  hasOpenedOnfido: boolean;
  onfidoModalOpen: boolean;
  handleRetryVerification: () => void;
};

const VerificationContext = createContext<VerificationContextType | null>(null);

export const useVerificationContext = () => {
  const context = useContext(VerificationContext);
  if (!context) {
    throw new Error('useVerificationContext must be used within VerificationWidget');
  }
  return context;
};

type VerificationWidgetInternalProps = VerificationWidgetProps & {
  children?: React.ReactNode;
};

const VerificationWidget: React.FC<VerificationWidgetInternalProps> = ({ emi, children }) => {
  const { formatMessage: t } = useIntl();
  const user = useSelector(getUserMeta);
  const emiMeta = useSelector((state: RootState) => getEMIAccountMeta(state, emi));
  const [getAccountUserMeta] = useLazyGetAccountUserMetaQuery();

  const [onfidoModalOpen, setOnfidoModalOpen] = useState(false);
  const [hasOpenedOnfido, setHasOpenedOnfido] = useState(false);
  const [onfidoClosedWithoutCompletion, setOnfidoClosedWithoutCompletion] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [hasOpenedStatusModal, setHasOpenedStatusModal] = useState(false);

  const isClientCreated = Object.keys(user.emiStatuses).includes(emi);
  const verificationStatus = emiMeta?.kyc_status;

  useEffect(() => {
    if (isClientCreated && (!emiMeta || Object.keys(emiMeta).length === 0)) {
      getAccountUserMeta({ emi });
    }
  }, [isClientCreated, emiMeta, emi, getAccountUserMeta]);

  const shouldOpenOnfido = verificationStatus === VerificationStatus.Init;

  const shouldShowStatusModal =
    verificationStatus === VerificationStatus.Pending ||
    verificationStatus === VerificationStatus.Rejected;

  useEffect(() => {
    if (isClientCreated && shouldOpenOnfido && !hasOpenedOnfido && emiMeta) {
      setOnfidoModalOpen(true);
      setHasOpenedOnfido(true);
      setOnfidoClosedWithoutCompletion(false);
    }
  }, [isClientCreated, shouldOpenOnfido, hasOpenedOnfido, emiMeta]);

  useEffect(() => {
    if (isClientCreated && shouldShowStatusModal && !hasOpenedStatusModal && emiMeta) {
      setStatusModalOpen(true);
      setHasOpenedStatusModal(true);
    }
  }, [isClientCreated, shouldShowStatusModal, hasOpenedStatusModal, emiMeta]);

  useEffect(() => {
    if (verificationStatus !== VerificationStatus.Init) {
      setOnfidoClosedWithoutCompletion(false);
    }
  }, [verificationStatus]);

  const handleOnfidoComplete = async () => {
    setOnfidoModalOpen(false);
    setOnfidoClosedWithoutCompletion(false);
    try {
      await getAccountUserMeta({ emi }).unwrap();
      message.success(
        t({ id: 'dashboard.verification.completed' }) || 'Verification completed successfully'
      );
    } catch (error) {
      console.error('Failed to complete verification process:', error);
      message.error(t({ id: 'dashboard.verification.error' }) || 'Failed to complete verification');
    }
  };

  const handleCloseOnfidoModal = () => {
    setOnfidoModalOpen(false);
    setHasOpenedOnfido(true);
    if (verificationStatus === VerificationStatus.Init) {
      setOnfidoClosedWithoutCompletion(true);
    }
  };

  const handleCloseStatusModal = () => {
    setStatusModalOpen(false);
    setHasOpenedStatusModal(true);
  };

  const handleRetryVerification = () => {
    setStatusModalOpen(false);
    setHasOpenedStatusModal(true);
    setOnfidoModalOpen(true);
    setOnfidoClosedWithoutCompletion(false);
  };

  const openOnfidoModal = useCallback(() => {
    if (shouldOpenOnfido) {
      setOnfidoModalOpen(true);
      setOnfidoClosedWithoutCompletion(false);
    }
  }, [shouldOpenOnfido]);

  return (
    <VerificationContext.Provider
      value={{
        openOnfidoModal,
        onfidoClosedWithoutCompletion,
        emi,
        hasOpenedOnfido,
        onfidoModalOpen,
        handleRetryVerification,
      }}
    >
      {children}
      <VerificationStatusModal
        status={verificationStatus || VerificationStatus.Init}
        open={statusModalOpen}
        onClose={handleCloseStatusModal}
        onAction={
          verificationStatus === VerificationStatus.Rejected
            ? handleRetryVerification
            : undefined
        }
        actionText={
          verificationStatus === VerificationStatus.Rejected
            ? t({ id: 'verification.retry.buttonLabel' }) || 'Retry Verification'
            : undefined
        }
      />

      <Modal
        open={onfidoModalOpen}
        footer={null}
        centered
        onCancel={handleCloseOnfidoModal}
        closable={true}
        mask={true}
        zIndex={1001}
        getContainer={() => document.body}
        width={600}
      >
        <OnfidoVerification
          emi={emi}
          onComplete={handleOnfidoComplete}
          closeVerificationModal={handleCloseOnfidoModal}
        />
      </Modal>
    </VerificationContext.Provider>
  );
};

export default VerificationWidget;

